import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, ChevronLeft, ChevronRight, Facebook, Image as ImageIcon } from "lucide-react";
import { BrandGraphic } from "@/components/BrandGraphic";
import api from "@/lib/api";

const FB_URL = "https://www.facebook.com/gebenezersda";

export default function Gallery() {
  const [allPhotos, setAllPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [dept, setDept] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setAllPhotos(r.data || []))
      .catch(() => setAllPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const departments = useMemo(() => [...new Set(allPhotos.map((p) => p.department || "General Church"))], [allPhotos]);
  const eventLabels = useMemo(() => [...new Set(allPhotos.map((p) => p.event_label).filter(Boolean))], [allPhotos]);
  const photos = useMemo(() => allPhotos.filter((p) =>
    (dept === "all" || (p.department || "General Church") === dept) &&
    (eventFilter === "all" || p.event_label === eventFilter)
  ), [allPhotos, dept, eventFilter]);

  // Group by event when no specific event is selected (photos without an event go last as "Church Life")
  const groups = useMemo(() => {
    if (eventFilter !== "all" || eventLabels.length === 0) return [{ label: null, items: photos.map((p, i) => ({ p, i })) }];
    const map = new Map();
    photos.forEach((p, i) => {
      const k = p.event_label || "";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push({ p, i });
    });
    const out = [...map.entries()].filter(([k]) => k).map(([k, items]) => ({ label: k, items }));
    if (map.has("")) out.push({ label: "Church Life", items: map.get("") });
    return out;
  }, [photos, eventFilter, eventLabels.length]);

  useEffect(() => { setLightboxIdx(null); }, [dept, eventFilter]);

  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);
  const goPrev = useCallback(() => setLightboxIdx((i) => (i > 0 ? i - 1 : photos.length - 1)), [photos.length]);
  const goNext = useCallback(() => setLightboxIdx((i) => (i < photos.length - 1 ? i + 1 : 0)), [photos.length]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, goPrev, goNext]);

  return (
    <div data-testid="gallery-page">
      <section className="bg-[#003B5C] py-28 lg:py-36 relative grain-overlay">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-6">Gallery</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white max-w-4xl">
            Life at Ebenezer.
          </h1>
          <p className="mt-8 text-lg text-white/75 max-w-2xl leading-relaxed">
            Moments from worship, fellowship, ministry, and the everyday life of our church family.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {loading ? (
            <div data-testid="gallery-loading" className="text-center py-20 text-[#1E1E1E]/60">Loading photos…</div>
          ) : allPhotos.length === 0 ? (
            <div data-testid="gallery-empty" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 order-2 lg:order-1">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-5">Photo Gallery</div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight mb-6">
                  More photos coming soon.
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-[#1E1E1E]/75 mb-8 max-w-2xl">
                  In the meantime, view our latest church photos, worship experiences, ministry
                  activities, and special events on our official Facebook page.
                </p>
                <a
                  href={FB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gallery-empty-facebook-cta"
                  className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-8 py-4 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#002A42] transition-all group"
                >
                  <Facebook size={16} className="text-[#D4A437]" />
                  View More on Facebook
                  <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>
              <div className="lg:col-span-5 order-1 lg:order-2">
                <div className="aspect-[4/3] rounded-sm overflow-hidden relative">
                  <BrandGraphic variant="logo">
                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-sm px-4 py-3 z-10">
                      <Facebook size={20} className="text-[#003B5C]" />
                      <span className="text-sm font-semibold text-[#003B5C]">@gebenezersda</span>
                    </div>
                  </BrandGraphic>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-10" data-testid="gallery-filters">
                <div className="flex flex-wrap gap-2">
                  {["all", ...departments].map((d) => (
                    <button
                      key={d}
                      onClick={() => setDept(d)}
                      data-testid={`gallery-filter-department-${d === "all" ? "all" : d.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`px-4 py-2 text-xs font-bold tracking-[0.14em] uppercase rounded-sm border transition-colors ${
                        dept === d ? "bg-[#003B5C] text-white border-[#003B5C]" : "bg-white text-[#003B5C] border-gray-200 hover:border-[#003B5C]"
                      }`}
                    >
                      {d === "all" ? "All Departments" : d}
                    </button>
                  ))}
                </div>
                {eventLabels.length > 0 && (
                  <select
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    data-testid="gallery-filter-event"
                    className="lg:ml-auto px-4 py-2.5 border border-gray-200 rounded-sm text-sm text-[#003B5C] bg-white focus:outline-none focus:border-[#003B5C]"
                  >
                    <option value="all">All Events</option>
                    {eventLabels.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                )}
              </div>

              {photos.length === 0 ? (
                <div data-testid="gallery-no-match" className="text-center py-16 text-[#1E1E1E]/60">No photos match these filters yet.</div>
              ) : groups.map((g) => (
                <div key={g.label || "__all"} className="mb-12" data-testid={g.label ? `gallery-group-${g.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}` : "gallery-group-all"}>
                  {g.label && (
                    <div className="flex items-end justify-between mb-5 border-b border-gray-100 pb-3">
                      <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#003B5C]">{g.label}</h2>
                      <span className="text-xs text-[#1E1E1E]/50 font-mono">{g.items.length} photo{g.items.length === 1 ? "" : "s"}</span>
                    </div>
                  )}
                  <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4 [column-fill:_balance]">
                    {g.items.map(({ p, i }) => (
                      <button
                        key={p.id}
                        onClick={() => openLightbox(i)}
                        data-testid={`gallery-photo-${p.id}`}
                        className="block w-full mb-3 sm:mb-4 break-inside-avoid overflow-hidden rounded-sm bg-[#F7F8FA] group cursor-zoom-in text-left"
                      >
                        <img
                          src={p.url}
                          alt={p.caption || "Garland Ebenezer church photo"}
                          loading="lazy"
                          className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        {p.caption && <div className="px-3 py-2 text-xs text-[#1E1E1E]/70 leading-snug">{p.caption}</div>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* View more on Facebook */}
              <div className="mt-16 lg:mt-20 pt-12 border-t border-gray-100 text-center">
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#003B5C] mb-3">
                  See more on Facebook.
                </h3>
                <p className="text-base text-[#1E1E1E]/65 mb-7 max-w-xl mx-auto">
                  Discover more church photos, worship moments, and ministry highlights on our official Facebook page.
                </p>
                <a
                  href={FB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gallery-facebook-cta"
                  className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-8 py-4 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#002A42] transition-all group"
                >
                  <Facebook size={16} className="text-[#D4A437]" />
                  View More on Facebook
                  <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && photos[lightboxIdx] && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} aria-label="Close" data-testid="lightbox-close" className="absolute top-5 right-5 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white bg-white/5 hover:bg-white/15 rounded-sm z-10">
              <X size={22} />
            </button>
            {photos.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label="Previous" data-testid="lightbox-prev" className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white bg-white/5 hover:bg-white/15 rounded-sm z-10">
                  <ChevronLeft size={24} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Next" data-testid="lightbox-next" className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white bg-white/5 hover:bg-white/15 rounded-sm z-10">
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            <motion.img
              key={photos[lightboxIdx].id}
              src={photos[lightboxIdx].url}
              alt={photos[lightboxIdx].caption || ""}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {photos[lightboxIdx].caption && (
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 max-w-2xl text-center text-white/85 text-sm bg-black/40 backdrop-blur-sm px-5 py-2 rounded-sm">
                {photos[lightboxIdx].caption}
              </div>
            )}
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/60 text-sm font-mono">
              {lightboxIdx + 1} / {photos.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
